import React, { useState, useMemo, useCallback } from 'react';
import { OKRState, WeeklyReview, WeeklySprintTask, MemberWeekSummary } from '../types';
import { computeSmartScore } from '../helpers';
import Card from 'components/card';
import { CheckCircle2, Lock, Unlock, ChevronDown, ChevronRight, Calendar, AlertTriangle, Plus } from 'lucide-react';
import { MdEdit } from 'react-icons/md';

const STORAGE_KEY = 'toola_okr_weekly_reviews';

const loadReviews = (): WeeklyReview[] => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]'); } catch { return []; }
};
const MAX_REVIEWS = 52; // Keep at most one year of weekly reviews
const saveReviews = (r: WeeklyReview[]) =>
    localStorage.setItem(STORAGE_KEY, JSON.stringify(r.slice(0, MAX_REVIEWS)));

// ── Week utilities ────────────────────────────────────────────────────────────

function getWeekBounds(offset = 0): { start: Date; end: Date; label: string } {
    const now = new Date();
    const monday = new Date(now);
    monday.setDate(now.getDate() - ((now.getDay() + 6) % 7) + offset * 7);
    monday.setHours(0, 0, 0, 0);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    sunday.setHours(23, 59, 59, 999);
    const weekNum = getWeekNumber(monday);
    const label = `Semaine ${weekNum} — ${monday.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}`;
    return { start: monday, end: sunday, label };
}

function getWeekNumber(d: Date): number {
    const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
    date.setUTCDate(date.getUTCDate() + 4 - (date.getUTCDay() || 7));
    const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
    return Math.ceil((((date.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
}

function isInWeek(dateStr: string, start: Date, end: Date): boolean {
    const d = new Date(dateStr);
    return d >= start && d <= end;
}

// ── Member summary auto-generation ───────────────────────────────────────────

function buildMemberSummary(state: OKRState, weekStart: Date, weekEnd: Date): MemberWeekSummary[] {
    return state.team.map((member) => {
        const planned = state.tasks.filter(
            (t) => t.assigneeId === member.id && isInWeek(t.dueDate, weekStart, weekEnd),
        );
        const done = planned.filter((t) => t.status === 'done');
        const slipped = planned.filter(
            (t) => t.status !== 'done' && new Date(t.dueDate) < new Date(),
        );
        return {
            memberId: member.id,
            memberName: member.name,
            planned: planned.length,
            done: done.length,
            slipped: slipped.length,
            tasksDone: done.map((t) => t.title),
            tasksSlipped: slipped.map((t) => t.title),
        };
    });
}

// ── Past review card ──────────────────────────────────────────────────────────

const PastReviewCard: React.FC<{ review: WeeklyReview }> = ({ review }) => {
    const [open, setOpen] = useState(false);
    const totalDone = review.memberSummaries.reduce((s, m) => s + m.done, 0);
    const totalSlipped = review.memberSummaries.reduce((s, m) => s + m.slipped, 0);

    return (
        <div className="rounded-xl border border-gray-100 bg-white">
            <button
                onClick={() => setOpen(!open)}
                className="w-full flex items-center gap-3 p-4 text-left hover:bg-gray-50 transition-colors"
            >
                {open ? <ChevronDown size={15} className="text-gray-400" /> : <ChevronRight size={15} className="text-gray-400" />}
                <div className="flex-1">
                    <p className="text-sm font-semibold text-navy-700">{review.weekLabel}</p>
                    <p className="text-xs text-gray-400">
                        {totalDone} tâche{totalDone > 1 ? 's' : ''} terminée{totalDone > 1 ? 's' : ''} · {totalSlipped} glissée{totalSlipped > 1 ? 's' : ''}
                    </p>
                </div>
                <Lock size={13} className="text-gray-300" />
            </button>
            {open && (
                <div className="px-4 pb-4 space-y-4 border-t border-gray-50 pt-3">
                    {review.memberSummaries.map((s) => (
                        <div key={s.memberId} className="flex items-start gap-3">
                            <div className="w-1.5 h-1.5 rounded-full bg-toola-400 mt-2 flex-shrink-0" />
                            <div className="flex-1">
                                <p className="text-sm font-medium text-navy-700">{s.memberName}</p>
                                <p className="text-xs text-gray-500">
                                    {s.done}/{s.planned} terminée{s.done > 1 ? 's' : ''}{s.slipped > 0 ? ` · ${s.slipped} glissée${s.slipped > 1 ? 's' : ''}` : ''}
                                </p>
                            </div>
                        </div>
                    ))}
                    <div className="rounded-xl bg-gray-50 p-3 space-y-2 text-sm">
                        {review.answers.blockers && (
                            <div><span className="font-semibold text-red-500">🚧 Blocages : </span><span className="text-gray-600">{review.answers.blockers}</span></div>
                        )}
                        {review.answers.adjustments && (
                            <div><span className="font-semibold text-orange-500">🔄 Ajustements : </span><span className="text-gray-600">{review.answers.adjustments}</span></div>
                        )}
                        {review.answers.topPriority && (
                            <div><span className="font-semibold text-toola-500">🎯 Priorité : </span><span className="text-gray-600">{review.answers.topPriority}</span></div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// ── Main component ────────────────────────────────────────────────────────────

const WeeklyReviewPanel: React.FC<{ state: OKRState; onAddTask: () => void }> = ({ state, onAddTask }) => {
    const [reviews, setReviews] = useState<WeeklyReview[]>(loadReviews);
    const [answers, setAnswers] = useState({ blockers: '', adjustments: '', topPriority: '' });
    const [selectedSprintTasks, setSelectedSprintTasks] = useState<Set<string>>(new Set());
    const [locked, setLocked] = useState(false);

    const { start, end, label } = useMemo(() => getWeekBounds(0), []);
    const nextWeek = useMemo(() => getWeekBounds(1), []);

    // Auto-generated summary for current week
    const summaries = useMemo(() => buildMemberSummary(state, start, end), [state, start, end]);

    // Tasks due this week for sprint selection
    const weekTasks = useMemo(
        () => state.tasks.filter((t) => isInWeek(t.dueDate, start, end) && t.status !== 'done'),
        [state.tasks, start, end],
    );

    // High-score tasks for next week suggestion
    const nextWeekSuggestions = useMemo(
        () =>
            [...state.tasks]
                .filter((t) => t.status !== 'done' && isInWeek(t.dueDate, nextWeek.start, nextWeek.end))
                .sort((a, b) => computeSmartScore(b) - computeSmartScore(a))
                .slice(0, 5),
        [state.tasks, nextWeek],
    );

    const currentReviewExists = reviews.some((r) => r.weekStart === start.toISOString());

    const toggleTask = (id: string) =>
        setSelectedSprintTasks((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });

    const handleLock = useCallback(() => {
        if (currentReviewExists) return;
        const review: WeeklyReview = {
            id: `review-${Date.now()}`,
            weekLabel: label,
            weekStart: start.toISOString(),
            weekEnd: end.toISOString(),
            sprintTasks: Array.from(selectedSprintTasks)
                .map((id) => state.tasks.find((t) => t.id === id))
                .filter(Boolean)
                .map((t) => ({ taskId: t!.id, title: t!.title, assigneeId: t!.assigneeId })),
            memberSummaries: summaries,
            answers,
            locked: true,
            createdAt: new Date().toISOString(),
        };
        const next = [review, ...reviews];
        setReviews(next);
        saveReviews(next);
        setLocked(true);
    }, [currentReviewExists, label, start, end, selectedSprintTasks, state.tasks, summaries, answers, reviews]);

    const textareaClass = 'w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-toola-500 resize-none';

    return (
        <div className="space-y-6">
            {/* Current week */}
            <Card extra="p-5">
                <div className="flex items-center justify-between mb-5">
                    <div>
                        <h3 className="text-base font-bold text-navy-700 flex items-center gap-2">
                            <Calendar size={17} className="text-toola-500" /> Revue hebdomadaire en cours
                        </h3>
                        <p className="text-sm text-gray-500 mt-0.5">{label}</p>
                    </div>
                    {locked || currentReviewExists
                        ? <span className="flex items-center gap-1.5 text-xs text-green-600 bg-green-50 px-3 py-1.5 rounded-full border border-green-200"><Lock size={12} /> Semaine verrouillée</span>
                        : <span className="flex items-center gap-1.5 text-xs text-orange-500 bg-orange-50 px-3 py-1.5 rounded-full border border-orange-200"><Unlock size={12} /> Non verrouillée</span>
                    }
                </div>

                {/* Member summaries */}
                <div className="mb-5">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Résumé automatique — semaine en cours</p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        {summaries.map((s) => {
                            const member = state.team.find((m) => m.id === s.memberId);
                            const pct = s.planned > 0 ? Math.round((s.done / s.planned) * 100) : 100;
                            const color = pct >= 80 ? '#17ad37' : pct >= 50 ? '#FFB547' : '#ea0606';
                            return (
                                <div key={s.memberId} className="rounded-xl border border-gray-100 p-3">
                                    <div className="flex items-center gap-2 mb-2">
                                        {member && (
                                            <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0" style={{ backgroundColor: member.avatarColor }}>
                                                {member.avatarInitials}
                                            </div>
                                        )}
                                        <p className="text-sm font-semibold text-navy-700 truncate">{s.memberName}</p>
                                    </div>
                                    <div className="flex items-center justify-between text-xs text-gray-500 mb-1.5">
                                        <span className="flex items-center gap-1"><CheckCircle2 size={11} className="text-green-500" /> {s.done} terminée{s.done > 1 ? 's' : ''}</span>
                                        {s.slipped > 0 && <span className="text-red-400 flex items-center gap-1"><AlertTriangle size={11} /> {s.slipped} glissée{s.slipped > 1 ? 's' : ''}</span>}
                                    </div>
                                    <div className="w-full h-1.5 rounded-full bg-gray-100"><div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: color }} /></div>
                                    <p className="text-xs text-gray-400 mt-1">{s.planned} planifiée{s.planned > 1 ? 's' : ''} cette semaine</p>
                                </div>
                            );
                        })}
                        {summaries.length === 0 && (
                            <p className="text-sm text-gray-400 col-span-3 text-center py-4">Aucun membre dans l'équipe</p>
                        )}
                    </div>
                </div>

                {/* Sprint selection */}
                <div className="mb-5">
                    <div className="flex items-center justify-between mb-3">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Sprint de la semaine — tâches verrouillées</p>
                        <button onClick={onAddTask} className="flex items-center gap-1 text-xs text-toola-500 hover:text-toola-600 font-medium">
                            <Plus size={13} /> Ajouter une tâche
                        </button>
                    </div>
                    <div className="space-y-1.5">
                        {weekTasks.map((task) => {
                            const member = state.team.find((m) => m.id === task.assigneeId);
                            const selected = selectedSprintTasks.has(task.id);
                            return (
                                <label key={task.id} className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 cursor-pointer transition-colors ${selected ? 'border-toola-200 bg-blue-50/50' : 'border-gray-100 hover:bg-gray-50'}`}>
                                    <input type="checkbox" checked={selected} onChange={() => toggleTask(task.id)} className="rounded text-toola-500" />
                                    <span className="text-sm text-navy-700 flex-1 truncate">{task.title}</span>
                                    {member && (
                                        <div className="w-5 h-5 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0" style={{ backgroundColor: member.avatarColor }}>
                                            {member.avatarInitials[0]}
                                        </div>
                                    )}
                                </label>
                            );
                        })}
                        {weekTasks.length === 0 && (
                            <p className="text-xs text-gray-400 text-center py-3">Aucune tâche planifiée cette semaine</p>
                        )}
                    </div>
                </div>

                {/* 3 questions */}
                <div className="space-y-4 mb-5">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
                        <MdEdit className="w-3.5 h-3.5" /> 3 questions de pilotage
                    </p>
                    {[
                        { key: 'blockers' as const, emoji: '🚧', label: "Qu'est-ce qui bloque ?" },
                        { key: 'adjustments' as const, emoji: '🔄', label: "Qu'est-ce qu'on ajuste ?" },
                        { key: 'topPriority' as const, emoji: '🎯', label: 'Quelle est la priorité de la semaine ?' },
                    ].map(({ key, emoji, label }) => (
                        <div key={key}>
                            <label className="text-sm font-medium text-navy-700 mb-1.5 block">{emoji} {label}</label>
                            <textarea
                                rows={2}
                                value={answers[key]}
                                onChange={(e) => setAnswers((prev) => ({ ...prev, [key]: e.target.value }))}
                                placeholder="Écrivez votre réponse…"
                                className={textareaClass}
                                disabled={locked || currentReviewExists}
                            />
                        </div>
                    ))}
                </div>

                {/* Next week suggestions */}
                {nextWeekSuggestions.length > 0 && (
                    <div className="rounded-xl bg-gray-50 p-3 mb-5">
                        <p className="text-xs font-semibold text-gray-500 mb-2">💡 Suggestions semaine prochaine (par smart score)</p>
                        <div className="space-y-1">
                            {nextWeekSuggestions.map((t) => {
                                const m = state.team.find((u) => u.id === t.assigneeId);
                                return (
                                    <div key={t.id} className="flex items-center gap-2 text-xs text-gray-600">
                                        <span className="font-bold text-orange-500">{computeSmartScore(t)}</span>
                                        <span className="flex-1 truncate">{t.title}</span>
                                        {m && <span className="text-gray-400">{m.name}</span>}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {!locked && !currentReviewExists && (
                    <button
                        onClick={handleLock}
                        className="w-full linear rounded-xl bg-toola-500 py-3 text-sm font-semibold text-white transition duration-200 hover:bg-toola-600 flex items-center justify-center gap-2"
                    >
                        <Lock size={15} /> Valider et verrouiller la semaine
                    </button>
                )}
            </Card>

            {/* History */}
            {reviews.length > 0 && (
                <div>
                    <p className="text-sm font-semibold text-gray-500 mb-3">Historique des revues</p>
                    <div className="space-y-2">
                        {reviews.map((r) => <PastReviewCard key={r.id} review={r} />)}
                    </div>
                </div>
            )}
        </div>
    );
};

export default WeeklyReviewPanel;
